/**
 * Take a deployed update on the visit it lands, not the visit after.
 *
 * <p>Both mobile apps are PWAs with a precached shell, which is what lets them
 * open with no network. The cost is that a returning browser is served the
 * *previous* build's HTML and JavaScript from the service worker's cache,
 * while the new worker installs quietly in the background. Nothing is wrong
 * with that page — it is simply the last one.
 *
 * <p>It stopped being a cosmetic delay when the build started carrying the
 * demo's sprint staging. `VITE_SPRINT` is compiled into the bundle, so an old
 * cached bundle is an old set of locks: a browser that had opened the site
 * before the staged build went out could still reach every screen, while a
 * fresh one saw sprint 1. The lock is only as current as the JavaScript
 * enforcing it.
 *
 * <p>So: `skipWaiting` and `clientsClaim` in the worker (see each app's
 * `vite.config.ts`) make the new worker activate and take over existing pages
 * immediately, and this listener reloads the page the moment it does. The
 * reload is what actually swaps the JavaScript — a worker taking control does
 * not re-execute a page that is already running.
 *
 * <p>Guarded against reloading twice: `controllerchange` also fires the first
 * time a worker takes control of a page that had none, and on a browser with
 * several tabs open each will claim in turn.
 */
export function reloadOnServiceWorkerUpdate(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  // No controller means no worker was driving this page, so this is a first
  // install rather than an update. Reloading there would restart a perfectly
  // current page — and on the very first visit, one the user just waited for.
  const hadController = navigator.serviceWorker.controller !== null
  let reloading = false

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloading) return
    reloading = true
    window.location.reload()
  })
}
